import { Form, Formik } from "formik";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { H1 } from "../../components/Heading";
import { Input } from "../../components/Input";
import { Paragraph } from "../../components/Paragraph";
import { ROUTE } from "../../routes";
import { Image } from "../../components/Image";
import tillerVerifySvg from "../../assets/verify-tiller.svg";
import { useState } from "react";
import loginSvg from "../../assets/login.svg";
import { useRecoveryCode } from "../../data/useRecoveryCode";
import { extractErrorMessage, showError } from "../../utils/error-handling";
import { end } from "@popperjs/core";
import { useRecoveryEmail } from "../../data/useRecoveryEmail";
import { notify } from "../../utils/notify";
import { getSafeReturnUrl } from "../../utils/helpers";
import { STORAGE_KEYS } from "../../utils/constants";

const RecoveryEmailOTP = () => {
  const [doubleClick, setDoubleClick] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;

  const [searchParams] = useSearchParams();
  const qsReturn = searchParams.get("returnurl") || "";

  const safeReturnUrl = getSafeReturnUrl(qsReturn);
  const suffix = safeReturnUrl
    ? `?returnurl=${encodeURIComponent(safeReturnUrl.replace(/^\//, ""))}`
    : "";

  const { mutate: recoveryEmail } = useRecoveryEmail();
  const { mutate: recoveryCode } = useRecoveryCode();

  const handleSubmit = (
    otp: string,
    setFieldError: (name: string, msg: string) => void
  ) => {
    setDoubleClick(true);
    recoveryCode(otp, {
      onSuccess: () => {
        setDoubleClick(false);
        window.sessionStorage.removeItem(STORAGE_KEYS.pendingReturnUrl);
        navigate(safeReturnUrl || ROUTE.APPLICATIONS, { replace: true });
      },
      onError: (err: any) => {
        const errorMessage = extractErrorMessage(
          err,
          "The code entered is invalid or already used."
        );
        setFieldError("otp", errorMessage);
        showError(err, "The code entered is invalid or already used.");
        setDoubleClick(false);
      },
    });
  };

  const sendEmailAgain = () => {
    recoveryEmail(email, {
      onSuccess: () => {
        setDoubleClick(false);
        notify("Email sent again!", {
          position: "bottom-left",
          type: "success",
        });
      },
      onError: (err: any) => {
        showError(err, "Soemthing went wrong, please try again!");
      },
    });
  };

  return (
    <Flex height="100vh">
      <Flex width="900px" flexDirection="column" p="60px" overflowY="auto">
        <Image
          src={tillerVerifySvg}
          alt="tiller verify logo"
          height="200px"
          width="160px"
        />

        <H1>Enter Your One-Time Password</H1>
        <Paragraph my={4}>
          If your email was found you should recieve a one time password. Please
          enter the code below to verify your identity and access your account.
        </Paragraph>

        <Paragraph my={4}>Sent to: {email}</Paragraph>
        <Formik
          initialValues={{ otp: "" }}
          onSubmit={(values, { setFieldError }) => {
            handleSubmit(values.otp, setFieldError);
          }}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <Flex
                gap="6"
                alignItems={"center"}
                justifyContent={"space-around"}
              >
                <Input
                  width={"100%"}
                  onChange={props.handleChange}
                  value={props.values.otp}
                  placeholder="Enter your one-time password"
                  label="Enter your one-time password"
                  name="otp"
                  isRequired
                  hasError={Boolean(props.errors.otp)}
                  errorMessage={props.errors.otp}
                />
              </Flex>
              <Flex my={2} justifyContent={"end"}>
                <Button
                  type="submit"
                  isDisabled={
                    !props.dirty ||
                    (props.dirty && !props.isValid) ||
                    doubleClick
                  }
                  mt="2"
                  size="large"
                  width="100%"
                >
                  Verify OTP
                </Button>
              </Flex>

              <Paragraph my={4}>
                I didn’t receive a one time password (this can time one minute
                to be received - please check your spam!).
              </Paragraph>

              <Flex justifyContent={end}>
                <Button p={0} variant="blueText" onClick={sendEmailAgain}>
                  Resend OTP
                </Button>
              </Flex>

              <Flex justifyContent={end}>
                <Button
                  p={0}
                  variant="blueText"
                  onClick={() =>
                    navigate(`${ROUTE.USE_RECOVERY_EMAIL}${suffix}`, {
                      replace: true,
                    })
                  }
                >
                  Input Email Address Again
                </Button>
              </Flex>
            </Form>
          )}
        </Formik>
      </Flex>
      <Flex
        bg={"background"}
        display={{ _: "none", md: "flex" }}
        height="100vh"
        width="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Image
          src={loginSvg}
          alt="login image"
          display="block"
          width="55%"
          height="60%"
        />
      </Flex>
    </Flex>
  );
};

export default RecoveryEmailOTP;
